import { useState } from 'react';

import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';

import { classNames } from '@/helpers/strings';

import { BUTTON_KIND } from '../Button';
import Modal from '../Modals/Modal';
import ModalFullWidth from '../Modals/ModalFullWidth';

const CLEAR_PROFILE_DISCLAIMERS = [
  {
    header: 'Profiles:',
    type: 'warning',
    bullet_points: ['All content and information will be cleared.'],
  },
  {
    header: 'Card:',
    type: 'info',
    bullet_points: ['Card remains functional.'],
  },
  {
    header: 'Access to the Tapt App:',
    type: 'warning',
    bullet_points: [
      'Profile owner will be logged out and all access will be revoked.',
      'Account and unsaved data will be inaccessible to the user.',
    ],
  },
  {
    header: 'Recycle empty profile:',
    type: 'info',
    bullet_points: [
      'New details can be applied to the cleared profile.',
      'Profile will be shared via existing QR code and link.',
    ],
  },
  {
    header: 'User analytics:',
    type: 'warning',
    bullet_points: ['User analytics will be cleared.'],
  },
  {
    header: 'Organisation & group analytics:',
    type: 'info',
    bullet_points: ['Organisation and group analytics will remain.'],
  },
  {
    header: 'Groups:',
    type: 'warning',
    bullet_points: ['Profile will be removed from associated group.'],
  },
  {
    header: 'Contacts:',
    type: 'info',
    bullet_points: ['Contacts will remain in the organisation contacts page.'],
  },
  {
    header: 'Active directory:',
    type: 'warning',
    bullet_points: ['Sync will be removed.'],
  },
];

export interface IWarningModal {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess: () => void;
  loading?: boolean;
}

export default function ClearProfileWarningModal({
  isOpen,
  setIsOpen,
  onSuccess,
  loading,
}: IWarningModal) {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  return (
    <>
      <ModalFullWidth
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        dialogTitle=""
        onSuccess={() => {
          setIsConfirmationModalOpen(true);
          setIsOpen(false);
        }}
        onCancel={() => {
          setIsOpen(false);
        }}
        onClose={() => {
          setIsOpen(false);
        }}
        successButtonText="Proceed to clear"
        cancelButtonText="Cancel"
        successButtonKind={BUTTON_KIND.RED}
        isLoading={loading}
      >
        <>
          <div className="border-b border-gray-200 pb-5">
            <h3 className="text-2xl text-gray-900 font-medium mb-2">
              <span className="font-semibold">Warning:</span> Consider the
              impact of clearing your profile.
            </h3>
            <p className="font-normal text-gray-500 text-base">
              Before proceeding, please take a moment to review the consequences
              of clearing your profile.
            </p>
          </div>
          <ul className="flex flex-row flex-wrap w-full">
            {CLEAR_PROFILE_DISCLAIMERS.map((disclaimer, index) => (
              <li
                key={`clear-profile-disclaimer-${index}`}
                className={classNames(
                  'w-5/12 flex-grow flex border-b border-gray-200 pb-2 mt-5',
                  index % 2 === 0 ? 'ml-5' : '',
                )}
              >
                {disclaimer.type === 'warning' && (
                  <XMarkIcon
                    className="h-6 w-6 flex-shrink-0 text-red-500"
                    aria-hidden="true"
                  />
                )}

                {disclaimer.type === 'info' && (
                  <CheckIcon
                    className="h-6 w-6 flex-shrink-0 text-emerald-500"
                    aria-hidden="true"
                  />
                )}
                <div className="ml-3">
                  <span className="text-gray-900 font-medium">
                    {disclaimer.header}
                  </span>
                  <ul className="list-disc text-gray-500 text-xs leading-5 font-normal">
                    {disclaimer.bullet_points.map((bullet_point, index) => (
                      <li
                        key={`clear-profile-disclaimer-bullet-point-${index}`}
                        className="ml-6"
                      >
                        {bullet_point}
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        </>
      </ModalFullWidth>

      <Modal
        isOpen={isConfirmationModalOpen}
        setIsOpen={setIsConfirmationModalOpen}
        dialogTitle=""
        successButtonText="Clear profile"
        onSuccess={() => {
          onSuccess();
          setIsConfirmationModalOpen(false);
        }}
        onCancel={() => {
          setIsOpen(false);
          setIsConfirmed(false);
        }}
        onClose={() => {
          setIsOpen(false);
          setIsConfirmed(false);
        }}
        successButtonKind={BUTTON_KIND.RED}
        isDisabled={!isConfirmed}
      >
        <div className="mb-5">
          <h3 className="text-2xl text-gray-900 font-medium mb-2">
            <span className="font-semibold">Warning:</span> Clearing this
            profile is permanent.
          </h3>
          <p className="font-normal text-gray-500 text-sm">
            Clearing this profile will permanently erase all associated data
            with no possibility of recovery.
          </p>
        </div>

        <div className="w-full flex justify-start space-x-4 items-center">
          <input
            id="clear-profile-confirmation-checkbox"
            type="checkbox"
            className="focus:ring-brand-500 h-4 w-4 text-brand-500 border-gray-300 rounded"
            defaultChecked={isConfirmed}
            onClick={() => {
              setIsConfirmed?.(!isConfirmed);
            }}
          />
          <label
            className="text-sm"
            htmlFor="clear-profile-confirmation-checkbox"
          >
            I am sure I want to clear this profile.
          </label>
        </div>
      </Modal>
    </>
  );
}
